import {
  Instant,
  LocalDate,
  LocalDateTime,
  LocalTime,
  ZoneId,
  ZoneOffset,
  ZonedDateTime,
} from 'js-joda';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { ConsumerStickyPanel } from 'components/ui/ConsumerStickyPanel';
import { DisplayDateTime } from '@tock/time';
import { EventDetailJsonLd } from 'jsonld/EventDetailJsonLd';
import { Head } from 'head/Head';
import { IWithContextProps, consumer, core, withContext } from '@tock/coretypes';
import { IconCalendar } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { Link, Typography } from 'omakase';
import { Route } from 'react-router';
import { SoldOutCard } from 'components/offering-detail-page/SoldOutCard';
import { canBeBooked } from 'utils/experience';
import { displayLocation } from '@tock/shared/utils/locationUtils';
import { getAltTextWithFallback } from 'utils/businessUtils';
import { isCutoffIntervalAfter } from 'utils/ticketTypeUtils';
import { isEventType, isOnlineOrdersType } from 'utils/offerings';
import { isLocationDisplayable } from 'utils/locationUtils';
import { offeringNaturalName, offeringPartyItem } from '@tock/shared/utils/offerings';
import { parse, stringify } from '@tock/query-string';
import CallOut from '@tock/shared/components/CallOut';
import ConsumerBorderCard from 'components/ui/ConsumerBorderCard';
import ConsumerPageHeading from 'components/ui/ConsumerPageHeading';
import IconCalendarLegacy from 'components/icons/svgr/IconCalendar';
import LargeImageGallery from 'components/ui/LargeImageGallery';
import Metadata from 'components/business/landing-page/Metadata';
import NewlineEscapedText from '@tock/shared/components/NewlineEscapedText';
import OfferingDetailSummary from 'components/offering-detail-page/OfferingDetailSummary';
import PageContent from 'components/ui/PageContent';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import React from 'react';
import SimpleReservationSearchBar from 'components/ui/SimpleReservationSearchBar';
import Wrapper from 'components/Wrapper';

interface Props {
  availablePurchaseSizes: number[];
  business: consumer.BusinessConfig;
  experience?: core.ITicketType;
  offering: consumer.Offering;
  ticketGroups: consumer.CalendarTicketGroup[];
  onSelectTickets(size: number): void;
  onSearch(partySize: number, selectedDate: string, selectedTime: string): void;
  onOpenWaitlist(): void;
}

class OfferingDetailContent extends PureBaseComponent<Props & IWithContextProps> {
  getSummaryProps() {
    const { offering } = this.props;

    const eventFirstDatetime = oc(offering).eventDetails.eventFirstDatetimeSecs()
      ? LocalDateTime.ofEpochSecond(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          oc(offering).eventDetails.eventFirstDatetimeSecs()!,
          ZoneOffset.UTC
        )
      : undefined;
    const eventLastDatetime = oc(offering).eventDetails.eventLastDatetimeSecs()
      ? LocalDateTime.ofEpochSecond(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          oc(offering).eventDetails.eventLastDatetimeSecs()!,
          ZoneOffset.UTC
        )
      : undefined;
    const schedule = oc(offering).eventDetails.schedule[0](undefined) as core.Schedule | undefined;
    const eventDate = oc(offering).eventDetails.date();
    const startTime = oc(offering).eventDetails.startTime();
    const endTime = oc(offering).eventDetails.endTime();
    return {
      schedule,
      eventFirstDatetime,
      eventLastDatetime,
      startDate: eventDate ? LocalDate.parse(eventDate) : undefined,
      startTime: startTime ? LocalTime.parse(startTime) : undefined,
      endTime: endTime ? LocalTime.parse(endTime) : undefined,
      price: oc(offering).price() || undefined,
      location: oc(offering).eventDetails.location() || undefined,
      isAtOneTime: offering.isAtOneTime,
      isOnOneDate: offering.isOnOneDate,
      offeringType: offering?.type,
    };
  }

  handleCollectImageData(action = 'Image gallery click') {
    const { business, offering } = this.props;
    window.analytics.sendEvent('Event detail page', action, `${business.name} - ${offering.id}`);
  }

  render() {
    const {
      availablePurchaseSizes,
      business,
      experience,
      offering,
      onOpenWaitlist,
      onSearch,
      onSelectTickets,
      ticketGroups,
    } = this.props;

    const summaryProps = this.getSummaryProps();

    const description = offering.description || '';
    const { services, ...businessMetadataFields } = business;
    const location = oc(offering).eventDetails.location({});
    const shouldDisplayAddress = isLocationDisplayable(location);
    const isAtBusinessLocation = oc(offering).isAtBusinessLocation(true);

    const subtitle = (
      <Route
        render={({ location: { search } }) => {
          const { previewMode, endDate } = parse(search);
          const href = `/${business.domainName}${
            previewMode ? `?${stringify({ previewMode, endDate })}` : ''
          }`;
          return (
            <div>
              <Link size="medium" href={href}>
                {business.name}
              </Link>{' '}
              {shouldDisplayAddress && <span> · {displayLocation(location)}</span>}
            </div>
          );
        }}
      />
    );

    let isEventInPast = false;
    if (
      summaryProps.eventLastDatetime &&
      summaryProps.eventLastDatetime.isBefore(LocalDateTime.now())
    ) {
      isEventInPast = true;
    }

    const canBook = canBeBooked(offering);

    const hasMultipleBookableTimes =
      oc(experience).numTicketGroups(0) > 1 || !(offering.isAtOneTime && offering.isOnOneDate);
    const documentTitle =
      `${offering.name} - ${business.name}` +
      `${displayLocation(location) ? ` - ${displayLocation(location)}` : ''} | Tock`;
    const eventImages = _.map(offering.image, (image) => {
      return {
        ...image,
        altText: getAltTextWithFallback(image.altText, business, offering),
      };
    });

    const secondsToStartTime = getSecondsToStartTime(
      summaryProps.startDate,
      summaryProps.startTime,
      business
    );
    const ticketGroup = getTicketGroupForExperience(ticketGroups, experience);
    const showEventCallOut =
      isCutoffIntervalAfter(experience) &&
      !hasMultipleBookableTimes &&
      ticketGroup !== undefined &&
      isEventType(offering.type) &&
      secondsToStartTime <= LocalTime.SECONDS_PER_HOUR;
    return (
      <PageContent className="PageContent--page-event">
        <EventDetailJsonLd offering={offering} business={business} />
        <Head
          title={documentTitle}
          description={offering.description}
          image={
            eventImages.length > 0 && eventImages[0].imageUrl ? eventImages[0].imageUrl : undefined
          }
          site={business.twitterHandle ?? ''}
          siteName={business.name ?? ''}
          type="event"
        />
        {!_.isNil(offering.image) && !_.isEmpty(offering.image) && (
          <div className="EventDetailContent-smallImageGallery">
            <LargeImageGallery
              business={business}
              experience={offering}
              onClickImage={this.handleCollectImageData}
              show={1}
              images={eventImages as core.UploadedImage[]}
              isLightboxEnabled={eventImages.length > 1}
            />
          </div>
        )}
        <div className="EventDetailContent">
          <Wrapper size="medium">
            <ConsumerPageHeading
              prefix={_.capitalize(offeringNaturalName(offering.type))}
              title={offering.name}
              subtitle={subtitle}
            />
          </Wrapper>
          <Wrapper size="medium">
            <div className="EventDetailContent-content">
              {!_.isNil(eventImages) && !_.isEmpty(eventImages) && (
                <div className="EventDetailContent-largeImageGallery">
                  <LargeImageGallery
                    business={business}
                    experience={offering}
                    onClickImage={this.handleCollectImageData}
                    show={eventImages.length >= 3 ? 3 : 1}
                    images={eventImages as core.UploadedImage[]}
                    isLightboxEnabled={eventImages.length > 1}
                  />
                </div>
              )}
              <div className="EventDetailContent-body">
                <div className="EventDetailContent-bodySummary">
                  <OfferingDetailSummary
                    {...summaryProps}
                    shouldDisplayAddress={shouldDisplayAddress}
                  />
                </div>
                <div className="EventDetailContent-bodyMobileDivider" />
                <div className="EventDetailContent-bodyDescription">
                  <p className="u-dont-break-out">
                    <Typography color="inherit" component="span" variant="body02">
                      <NewlineEscapedText text={description} linkify={true} />
                    </Typography>
                  </p>
                </div>
                <div className="EventDetailContent-bodyNonMobileDivider" />
                <div className="EventDetailContent-bodyMobileDivider" />
                {location && (
                  <Metadata
                    {...businessMetadataFields}
                    address={location}
                    isAtBusinessLocation={isAtBusinessLocation}
                    shouldDisplayAddress={shouldDisplayAddress}
                  />
                )}
              </div>
            </div>
            <ConsumerStickyPanel>
              <ConsumerBorderCard className="EventDetailContent-sideSummary">
                <div className="EventDetailContent-sideSummaryHeader">
                  <div className="EventDetailContent-sideSummaryHeaderContent">
                    <Typography component="span" variant="body02Strong">
                      {_.capitalize(offeringNaturalName(offering.type))} details
                    </Typography>
                  </div>
                </div>
                <div className="EventDetailContent-sideSummaryBody">
                  <OfferingDetailSummary
                    {...summaryProps}
                    shouldDisplayAddress={shouldDisplayAddress}
                  />
                </div>
              </ConsumerBorderCard>
              {canBook && !isEventInPast && (
                <ConsumerBorderCard className="EventDetailContent-sideSearch">
                  <SimpleReservationSearchBar
                    submitButtonLabel={
                      offering.isOnOneDate
                        ? offeringPartyItem(offering.type) === 'ticket'
                          ? `Get tickets`
                          : isOnlineOrdersType(offering)
                            ? 'Order now'
                            : 'Book now'
                        : 'Choose a date'
                    }
                    submitText={
                      offering.isOnOneDate
                        ? offeringPartyItem(offering.type) === 'ticket'
                          ? `Get tickets`
                          : isOnlineOrdersType(offering)
                            ? 'Order now'
                            : 'Book now'
                        : 'Choose a date'
                    }
                    submitButtonIcon={
                      !offering.isOnOneDate && (
                        <IconMigrationUtil
                          legacyIcon={
                            <IconCalendarLegacy className="EventDetailContent-dateIcon" />
                          }
                          newIcon={<IconCalendar />}
                        />
                      )
                    }
                    isSubmitDisabled={!canBook}
                    minGuestSize={_.min(availablePurchaseSizes) || 0}
                    maxGuestSize={_.max(availablePurchaseSizes) || 0}
                    onSubmit={hasMultipleBookableTimes ? onSearch : onSelectTickets}
                    partySizeUnit={offeringPartyItem(offering.type, offering.descriptiveVariety)}
                  />
                </ConsumerBorderCard>
              )}
              {!canBook && !isEventInPast && (
                <SoldOutCard
                  businessName={business.name}
                  offeringName={offering.name}
                  onJoinWaitlist={business.showWaitlist ? onOpenWaitlist : undefined}
                />
              )}
              {isEventInPast && (
                <ConsumerBorderCard className="EventDetailContent-sideSearch">
                  <Typography variant="caption" component="span">
                    {offering.name} at {business.name} is no longer offered.
                  </Typography>
                </ConsumerBorderCard>
              )}
              {canBook && showEventCallOut && (
                <EventAfterStartCallout
                  cutoffTime={ticketGroup?.cutoffTimeSecs}
                  businessTimeZone={business.timeZone}
                  secondsToStartTime={secondsToStartTime}
                />
              )}
            </ConsumerStickyPanel>
          </Wrapper>
        </div>
      </PageContent>
    );
  }
}

function getSecondsToStartTime(
  startDate: LocalDate | undefined,
  startTime: LocalTime | undefined,
  business: consumer.BusinessConfig
): number {
  const startDateTime = LocalDateTime.of(
    startDate ?? LocalDate.now(),
    startTime ?? LocalTime.now()
  );
  const zonedStartDateTime = ZonedDateTime.of(startDateTime, ZoneId.of(business.timeZone));
  const zoneDateTimeNow = ZonedDateTime.now();
  return zonedStartDateTime.toEpochSecond() - zoneDateTimeNow.toEpochSecond();
}

function getTicketGroupForExperience(
  ticketGroups: consumer.CalendarTicketGroup[],
  experience: core.ITicketType | undefined
): consumer.CalendarTicketGroup | undefined {
  return ticketGroups !== undefined
    ? _.find(ticketGroups, (ticketGroup) =>
        _.some(
          ticketGroup.ticketTypePrice,
          (ticketTypePrice) => ticketTypePrice.ticketTypeId === experience?.id ?? 0
        )
      )
    : undefined;
}

interface EventAfterStartCalloutProps {
  cutoffTime: number | undefined;
  businessTimeZone: string;
  secondsToStartTime: number;
}

const EventAfterStartCallout: React.FunctionComponent<EventAfterStartCalloutProps> = ({
  cutoffTime,
  businessTimeZone,
  secondsToStartTime,
}) => {
  const cutoffDateTimeUTC = ZonedDateTime.ofInstant(
    Instant.ofEpochSecond(cutoffTime ?? Instant.now().epochSecond()),
    ZoneId.UTC
  );
  const cutoffDateTime = cutoffDateTimeUTC.withZoneSameInstant(ZoneId.of(businessTimeZone));
  const now = LocalDateTime.now();
  const today = cutoffDateTime.dayOfMonth() === now.dayOfMonth() ? '(today)' : '';
  const calloutMessage =
    secondsToStartTime <= 0
      ? 'Event has started, but tickets are still available until'
      : 'This event starts soon, but tickets will be available until';
  return (
    <CallOut>
      {calloutMessage}&nbsp;
      <DisplayDateTime dateTimeValue={cutoffDateTime.toLocalDateTime().toString() ?? ''} />
      &nbsp;{today}
    </CallOut>
  );
};
export default withContext(OfferingDetailContent);
